/**
 * Sales credit layer (docs/design/sales-credit-model.md, Sasha 10/10).
 *
 * One deal, two independent factors:
 *
 *   share   WHO is credited. The owner carries 100% unless a credit split
 *           exists (finmon.new_business_split); then each participant carries
 *           their percent. Shares apply in every mode, to lives, to groups and
 *           to new brokers sold, so salespeople always sum to the company.
 *   weight  WHAT the deal is worth. The New Business bucket weight on lives
 *           and the group category weight on groups. A WON deal with a stamp
 *           reads the stamp (frozen, overrides respected): that is the final
 *           word. Anything else is classified here from the opportunity's
 *           Products to Quote, labelled provisional, and is replaced by the
 *           stamp the day the deal closes.
 *
 * The Weighted / Actual / Both toggle controls the weight only.
 *
 * Pure and browser-safe: pages and the Function both call it with the data
 * they already hold. Nothing here fetches.
 */

import type { NewBusinessValue } from '../../entities/admin-config/model/types';
import { classifySignals, creditedLives, resolveGroupCategory } from '../new-business/classify';
import { effectiveValues } from '../new-business/assemble';
import { buildSignals } from '../new-business/signals';
import type { NewBusinessAdjustment, NewBusinessCreditSplit, NewBusinessStamp } from '../new-business/types';

export type CreditMode = 'weighted' | 'actual' | 'both';

export const CREDIT_MODES: readonly CreditMode[] = ['weighted', 'actual', 'both'];

export const CREDIT_MODE_LABELS: Record<CreditMode, string> = {
  weighted: 'Weighted',
  actual: 'Actual',
  both: 'Both',
};

/** Where a deal's weight came from. */
export type CreditProvenance = 'stamped' | 'provisional' | 'unclassified';

/**
 * 'single': one salesperson. 'not-set': a co-seller and no split (owner 100%).
 * 'set': a split applies. 'drift': a split exists but Sales Hub has moved on
 * (the owner changed, or a share holder left the Sales Team), so the owner
 * carries 100% until someone re-enters it (design doc, "Drift").
 */
export type CreditSplitStatus = 'single' | 'not-set' | 'set' | 'drift';

export interface CreditParticipant {
  userId: string;
  userName: string;
  /** Fraction 0..1. Participants on one deal sum to 1. */
  share: number;
}

export interface DealCredit {
  opportunityId: string;
  /**
   * EE lives before the weight. A stamped deal reads the lives frozen at close
   * plus its adjustments (Sasha 10/14: the close size stands until an addendum
   * corrects it from the live group); anything else reads Sales Hub's EE count
   * today. 0 when blank.
   */
  actualLives: number;
  /** Bucket weight applied to lives. */
  weight: number;
  /** Lives after the weight: the stamp's credited lives (override included) plus adjustments, or actual x weight. */
  weightedLives: number;
  /** How many groups the deal counts as in weighted mode (group category weight). */
  groupWeight: number;
  bucketKey: string;
  bucketLabel: string;
  provenance: CreditProvenance;
  participants: CreditParticipant[];
  splitStatus: CreditSplitStatus;
}

export interface DealCreditInput {
  opportunityId: string;
  ownerId: string | null | undefined;
  ownerName: string | null | undefined;
  numberOfEmployees: number | null | undefined;
  /** `Opportunity.productsToQuote` labels (the dts_* toggles as the browser sees them). */
  productsToQuote: readonly string[];
  /** Products Sold labels from the close activity, when the caller has them. */
  soldLabels?: readonly string[];
  /** The New Business stamp for a won deal, when one exists. */
  stamp?: NewBusinessStamp | null;
  /** This deal's New Business adjustments (addenda); read only with a stamp. */
  adjustments?: readonly NewBusinessAdjustment[];
  /** Sales Team members besides the owner (Sales Hub connections). */
  coSellers?: readonly { userId: string; userName: string }[];
  /** False when the Sales Team could not be read; see splitDrifted. Defaults to true. */
  coSellersKnown?: boolean;
  /** The current credit split version, when one exists. */
  split?: NewBusinessCreditSplit | null;
}

const lower = (s: string | null | undefined): string => (s ?? '').toLowerCase();

/**
 * Has Sales Hub moved on since the split was entered? True when the owner is
 * not among the split's people (the deal was reassigned) or when someone
 * holding a share is neither the owner nor on the Sales Team any more. The
 * design doc's rule: the row stays, the deal is flagged, and the owner-100%
 * default applies until someone re-enters the split. Without this check a
 * reassigned deal kept crediting the previous owner under a green badge.
 */
export function splitDrifted(
  ownerId: string | null | undefined,
  coSellers: readonly { userId: string }[] | undefined,
  split: NewBusinessCreditSplit | null | undefined,
  /**
   * False when the Sales Team could not be read at all (a Vera-only role
   * without `connection` read, or a failed query). The membership half of
   * the check is then skipped, so a split still applies and every reader
   * sees the same numbers; only the owner-changed half can fire.
   */
  coSellersKnown: boolean = true,
): boolean {
  if (!split) return false;
  const owner = lower(ownerId);
  const named = split.shares.map((s) => lower(s.userId));
  if (owner && !named.includes(owner)) return true;
  if (!coSellersKnown) return false;
  const onDeal = new Set<string>([owner, ...(coSellers ?? []).map((c) => lower(c.userId))].filter(Boolean));
  return split.shares.some((s) => s.share > 0 && !onDeal.has(lower(s.userId)));
}

/** Participants and their shares: the split when set and still valid, else the owner at 100%. */
export function participantsOf(
  input: Pick<DealCreditInput, 'ownerId' | 'ownerName' | 'coSellers' | 'split' | 'coSellersKnown'>,
): {
  participants: CreditParticipant[];
  splitStatus: CreditSplitStatus;
} {
  const hasCoSellers = (input.coSellers?.length ?? 0) > 0;
  const drifted = splitDrifted(input.ownerId, input.coSellers, input.split, input.coSellersKnown ?? true);
  const shares = drifted ? [] : input.split?.shares.filter((s) => s.share > 0) ?? [];
  if (shares.length > 0) {
    const total = shares.reduce((acc, s) => acc + s.share, 0) || 100;
    return {
      participants: shares.map((s) => ({ userId: lower(s.userId), userName: s.userName, share: s.share / total })),
      splitStatus: 'set',
    };
  }
  const participants: CreditParticipant[] = input.ownerId
    ? [{ userId: lower(input.ownerId), userName: input.ownerName ?? '(unknown)', share: 1 }]
    : [];
  return { participants, splitStatus: drifted ? 'drift' : hasCoSellers ? 'not-set' : 'single' };
}

/**
 * Derive one deal's credit. Stamped deals read the stamp; everything else is
 * classified from the opportunity's signals with the current config.
 */
export function deriveDealCredit(input: DealCreditInput, config: NewBusinessValue): DealCredit {
  const liveLives = input.numberOfEmployees != null && Number.isFinite(input.numberOfEmployees) ? input.numberOfEmployees : 0;
  const { participants, splitStatus } = participantsOf(input);

  if (input.stamp) {
    // The stamp is the close size; adjustments are the only way it moves, so
    // a resize in Sales Hub after close does not leak in on one side only.
    // The New Business report reads the same figures.
    const v = effectiveValues(input.stamp, config);
    let deltaActual = 0;
    let deltaCredited = 0;
    for (const a of input.adjustments ?? []) {
      deltaActual += a.deltaActualLives;
      deltaCredited += a.deltaCreditedLives;
    }
    return {
      opportunityId: input.opportunityId,
      actualLives: round2((input.stamp.actualLives ?? liveLives) + deltaActual),
      weight: v.weight,
      weightedLives: round2(v.creditedLives + deltaCredited),
      groupWeight: resolveGroupCategory(v.bucketKey, config).weight,
      bucketKey: v.bucketKey,
      bucketLabel: v.bucketLabel,
      provenance: 'stamped',
      participants,
      splitStatus,
    };
  }

  const signals = buildSignals({ productLabels: input.productsToQuote, soldLabels: input.soldLabels ?? [] });
  const c = classifySignals(signals, config);
  return {
    opportunityId: input.opportunityId,
    actualLives: liveLives,
    weight: c.weight,
    weightedLives: creditedLives(liveLives, c.weight),
    groupWeight: resolveGroupCategory(c.bucketKey, config).weight,
    bucketKey: c.bucketKey,
    bucketLabel: c.label,
    provenance: c.unclassified ? 'unclassified' : 'provisional',
    participants,
    splitStatus,
  };
}

/** A salesperson's lives and groups from one deal in one reading. */
export interface CreditFigures {
  groups: number;
  lives: number;
}

export function figuresFor(deal: DealCredit, share: number, reading: 'weighted' | 'actual'): CreditFigures {
  if (reading === 'weighted') return { groups: round2(share * deal.groupWeight), lives: round2(share * deal.weightedLives) };
  return { groups: round2(share), lives: round2(share * deal.actualLives) };
}

/**
 * The share of a deal that belongs to one salesperson (or team), summed over
 * the participants `isRep` accepts. 0 when none of them is on the deal; a
 * co-seller with no split entered is not a participant and reads 0 here.
 */
export function shareForRep(deal: DealCredit, isRep: (userId: string) => boolean): number {
  let share = 0;
  for (const p of deal.participants) if (isRep(p.userId)) share += p.share;
  return share > 1 ? 1 : share;
}

/** Both readings at once, for a page that renders "actual / weighted". */
export function bothFiguresFor(deal: DealCredit, share: number): { actual: CreditFigures; weighted: CreditFigures } {
  return { actual: figuresFor(deal, share, 'actual'), weighted: figuresFor(deal, share, 'weighted') };
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

/**
 * Whole numbers plain, fractions to at most two decimals: 1,240 / 0.5 / 0.25 /
 * 123.5. Two, not one, so a 50/50 split of a 0.5 group reads 0.25 + 0.25 and
 * still visibly sums to the company figure.
 */
export function formatCredit(n: number): string {
  if (!Number.isFinite(n)) return '';
  if (Number.isInteger(n)) return n.toLocaleString();
  return n.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 2 });
}

/** Counts over a set of deals for a footnote: how many carry a second salesperson, how many of those lack a split or carry a drifted one, how many are provisional. */
export function creditNotes(deals: Iterable<DealCredit>): { coSold: number; splitNotSet: number; drift: number; provisional: number; unclassified: number } {
  let coSold = 0;
  let splitNotSet = 0;
  let drift = 0;
  let provisional = 0;
  let unclassified = 0;
  for (const d of deals) {
    if (d.splitStatus !== 'single') coSold += 1;
    if (d.splitStatus === 'not-set') splitNotSet += 1;
    if (d.splitStatus === 'drift') drift += 1;
    if (d.provenance === 'provisional') provisional += 1;
    if (d.provenance === 'unclassified') unclassified += 1;
  }
  return { coSold, splitNotSet, drift, provisional, unclassified };
}
